// React and React Router
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useHistory } from "react-router-dom";

// Material UI Core Components and Icons
import {
  Box,
  Typography,
  Stepper,
  Step,
  StepLabel,
  CircularProgress,
  makeStyles,
  AppBar,
  Toolbar,
  Divider,
} from "@material-ui/core";
import { Description, Edit, Note, ViewAgenda } from "@material-ui/icons";

// Custom Components
import ProjectDetails from "./Components/ProjectDetails";
import ReviewJson from "./Components/ReviewJson";
import FormEntries from "./Components/FormEntries";
import PreviewForm from "./Components/PreviewForm";
import HeadingWithDescription from "components/Heading/HeadingWithDescription";

// API Utilities
import {
  getJSONProjectAPI,
  updateJSONProjectAPI, setNextStepJsonProjectAPI,
  saveNewMDInJSONProjectAPI, convertMarkdownToSurveyJSFormAPI,
  saveNewJSONDesignInJSONProjectAPI,
  publishFormAPI, unPublishFormAPI
} from "views/APIs/APIUtility";
import { fetchAllProjectsAPI, getUserDevValProjectsAPI } from "views/APIs/ProjectsUtility";

// Styles
import styles from "assets/jss/material-dashboard-pro-react/views/dashboardStyle.js";
import { stepperStyles, QontoConnector } from "views/Styles/stepperStyles";
import appbarStyles from "views/Styles/appbarStyles";

// Contexts
import NotificationContext from "views/Components/Context.js";
import { Auth } from "aws-amplify";
import ReviewScreen from "../ReviewScreen";

const useStyles = makeStyles((theme) => ({
  ...styles,
  ...stepperStyles(theme),
  ...appbarStyles(theme),
}));

const LoadingStepIcon = () => <CircularProgress size={16} />;

export default function FormsBoard(props) {
  const history = useHistory();
  const classes = useStyles();
  const { showNotification } = React.useContext(NotificationContext);
  const [projects, setProjects] = useState([]);
  const [userDevValProjects, setUserDevValProjects] = useState([]);
  const [formSteps, setFormSteps] = useState([
    { label: "Project Details", icon: <Description />, lockedMessage: "" },
    {
      label: "Extract Content",
      icon: <ViewAgenda />,
      lockedMessage:
        "Please review the project details to move on to the next step.",
    },
    {
      label: "Review Form",
      icon: <ViewAgenda />,
      lockedMessage: "Please review the markdown to move on to the next step.",
    },
    {
      label: "Preview Form",
      icon: <Edit />,
      lockedMessage: "Please review the json to move on to the next step.",
    },
    {
      label: "Form Entries",
      icon: <Note />,
      lockedMessage:
        "",
    },
  ]);

  const [statusToStepMapping, setStatusToStepMapping] = useState({
    "Project Details": 0,
    "Extract Content": 1,
    "Review Form": 2,
    "Preview Form": 3,
    "Form Entries": 4,
    default: 0,
  });

  // State variables
  const [contentStep, setContentStep] = useState(0); // drives content
  const [formCompletedTillStep, setFormCompletedTillStep] = useState(0);
  const [visitedSteps, setVisitedSteps] = useState([0]);
  const [boardLoading, setBoardLoading] = useState({
    show: false,
    type: "",
    message: ""
  });
  const [formName, setFormName] = useState("");
  const [form, setForm] = useState(
    {
      "_id": {
        "$oid": "123"
      },
      "name": "Model Development Form Sample",
      "description": "Model Development Form",
      "uploaded_files": [
        {
          "resource_id": {
            "$oid": "234"
          },
          "resource_type": "File",
          "resource_name": "Data Card.pdf",
          "resource_description": "This is a resource file uploaded at the time of lab creation.",
          "resource_link": "https://qucoursify.s3.us-east-1.amazonaws.com/qu-form-design/68ac7bc19960211acb43b862/Data%20Card.pdf"
        }
      ],
      "timestamp": "2025-08-25T11:05:37.305Z",
      "status": "Extract Content",
      "current_step": 1,
      "users": [
        "4cee3247-32fc-4c6f-8014-1b9471d94970"
      ],
      "json_design": [
        {
          "version": 1,
          "json": {
            "title": "Customer Feedback Survey",
            "description": "Collect basic information to kick off a new project.",
            "showQuestionNumbers": "on",
            "showProgressBar": "top",
            "pages": [
              {
                "name": "general",
                "title": "General",
                "elements": [
                  {
                    "type": "text",
                    "name": "project-name",
                    "title": "Project name",
                    "isRequired": true,
                    "placeholder": "e.g., Phoenix"
                  },
                  {
                    "type": "dropdown",
                    "name": "department",
                    "title": "Department",
                    "choices": [
                      "Engineering",
                      "Product",
                      "Marketing",
                      "Finance",
                      "Operations",
                      "Legal"
                    ],
                    "isRequired": true
                  },
                  {
                    "type": "text",
                    "name": "target-launch",
                    "title": "Target launch date",
                    "inputType": "date"
                  },
                  {
                    "type": "boolean",
                    "name": "needs-budget-approval",
                    "title": "Does this project need budget approval?",
                    "labelTrue": "Yes",
                    "labelFalse": "No"
                  }
                ]
              },
              {
                "name": "scope",
                "title": "Scope",
                "elements": [
                  {
                    "type": "checkbox",
                    "name": "deliverables",
                    "title": "Deliverables in scope",
                    "choices": [
                      "Web app",
                      "Mobile app",
                      "API",
                      "Data pipeline",
                      "Dashboard",
                      "Automation"
                    ],
                    "isRequired": true,
                    "validators": [
                      {
                        "type": "answercount",
                        "minCount": 1
                      }
                    ]
                  },
                  {
                    "type": "comment",
                    "name": "success-criteria",
                    "title": "Define success criteria",
                    "isRequired": true,
                    "maxLength": 500
                  },
                  {
                    "type": "rating",
                    "name": "project-risk",
                    "title": "Perceived risk level",
                    "rateMin": 1,
                    "rateMax": 5,
                    "minRateDescription": "Low",
                    "maxRateDescription": "High"
                  }
                ]
              },
              {
                "name": "contacts",
                "title": "Contacts",
                "elements": [
                  {
                    "type": "text",
                    "name": "requester-name",
                    "title": "Requester name",
                    "isRequired": true
                  },
                  {
                    "type": "text",
                    "name": "requester-email",
                    "title": "Requester email",
                    "inputType": "email",
                    "isRequired": true,
                    "validators": [
                      {
                        "type": "email"
                      }
                    ]
                  },
                  {
                    "type": "text",
                    "name": "stakeholders",
                    "title": "Key stakeholders (comma-separated)"
                  }
                ]
              }
            ],
            "completedHtml": "<h3>Thanks! We'll review your intake shortly.</h3>"
          }
        },
        {
          "version": 2,
          "json": {
            "title": "Customer Satisfaction Survey",
            "showProgressBar": "top",
            "pages": [
              {
                "name": "experience",
                "title": "Your Experience",
                "elements": [
                  {
                    "type": "rating",
                    "name": "overall-satisfaction",
                    "title": "Overall, how satisfied are you with your experience today?",
                    "rateMin": 0,
                    "rateMax": 10,
                    "minRateDescription": "0 - Not at all",
                    "maxRateDescription": "10 - Extremely"
                  },
                  {
                    "type": "radiogroup",
                    "name": "resolved",
                    "title": "Was your issue resolved?",
                    "isRequired": true,
                    "choices": [
                      "Yes",
                      "No",
                      "Partially"
                    ]
                  },
                  {
                    "type": "comment",
                    "name": "details",
                    "title": "Tell us more about your rating (optional)"
                  }
                ]
              },
              {
                "name": "channel",
                "title": "About You",
                "elements": [
                  {
                    "type": "dropdown",
                    "name": "contact-channel",
                    "title": "How did you contact us?",
                    "choices": [
                      "Email",
                      "Chat",
                      "Phone",
                      "In-app",
                      "Social media"
                    ]
                  },
                  {
                    "type": "checkbox",
                    "name": "product-areas",
                    "title": "Which product areas did your feedback involve?",
                    "choices": [
                      "Billing",
                      "Performance",
                      "Usability",
                      "Features",
                      "Security",
                      "Other"
                    ]
                  },
                  {
                    "type": "text",
                    "name": "email-optin",
                    "title": "Email (only if you'd like us to follow up)",
                    "inputType": "email",
                    "validators": [
                      {
                        "type": "email"
                      }
                    ],
                    "visibleIf": "{resolved} = 'No' or {resolved} = 'Partially'"
                  }
                ]
              },
              {
                "name": "consent",
                "title": "Consent",
                "elements": [
                  {
                    "type": "boolean",
                    "name": "consent-research",
                    "title": "May we use your feedback for research to improve the product?",
                    "labelTrue": "Yes",
                    "labelFalse": "No",
                    "isRequired": true
                  }
                ]
              }
            ],
            "triggers": [
              {
                "type": "complete",
                "expression": "{overall-satisfaction} >= 9 and {resolved} = 'Yes'"
              }
            ],
            "completedHtml": "<h3>Thanks for your feedback!</h3><p>Your responses have been recorded.</p>"
          }
        }
      ],
      "markdown_md": [
        {
          "version": 1,
          "markdown": "temp",
          "timestamp": "2025-08-26T10:52:28.521Z"
        }
      ],
      "associated_project_id": "68adbe50be8421a96cfcf4ab"
    }
  );


  const [reviewScreenFeedback, setReviewScreenFeedback] = useState("");
  const [reviewScreenFeedbackFiles, setReviewScreenFeedbackFiles] = useState([]);
  const [currentMarkdownVersion, setCurrentMarkdownVersion] = useState(0);
  const [latestMarkdown, setLatestMarkdown] = useState(form["markdown_md"].at(0)['markdown']);
  const [currentJsonVersion, setCurrentJsonVersion] = useState(0);
  const [latestJsonObj, setLatestJsonObj] = useState(form.json_design.at(0)['json']);
  const [originalMarkdownContent, setOriginalMarkdownContent] = useState(form["markdown_md"].at(0)['markdown']);


  const [formGeneration, setFormGeneration] = useState(false);

  const { formID } = props.match.params;

  // In-between loader messages (LOCAL)
  const inBetweenStagesLoaderOptions = {

  };
  const [inBetweenStagesLoaderMessage, setInBetweenStagesLoaderMessage] =
    useState("Loading...");

  // Auth user
  const [user, setUser] = useState("");

  // Only suppress the **content** loader after user clicks back; Stepper stays in loading
  const [suppressContentOnly, setSuppressContentOnly] = useState(false);

  // Derived flags
  const isInBetween = useMemo(
    () => Object.prototype.hasOwnProperty.call(inBetweenStagesLoaderOptions, form.status),
    [form.status]
  );

  // Big expected-time line
  const WAIT_MSG = useMemo(() => {
    if (!isInBetween) return "";
    const min = 10 + Math.floor(Math.random() * 5);
    const max = 15 + Math.floor(Math.random() * 6);
    return `Expected time: ${min}-${max} minutes. You can leave this tab; processing will continue in the background.`;
  }, [isInBetween]);

  // Step index from current status (base)
  const baseStepIndex = useMemo(() => {
    const raw = statusToStepMapping[form.status];
    return Number.isInteger(raw) ? raw : statusToStepMapping.default;
  }, [form.status, statusToStepMapping]);

  // Stepper behavior
  const stepperInBetween = isInBetween; // stepper always shows loading when in-between
  const contentInBetween = isInBetween && !suppressContentOnly; // content can hide loader after back nav

  // Visual step for the Stepper (pin to loading bubble if in-between)
  const visualActiveStep = useMemo(
    () => (stepperInBetween ? baseStepIndex + 1 : contentStep),
    [stepperInBetween, baseStepIndex, contentStep]
  );

  // Build steps with an inserted Loading… pseudo-step
  const stepsWithIntermediate = useMemo(() => {
    if (!stepperInBetween) return formSteps;
    const insertAt = baseStepIndex + 1;
    const loadingStep = { label: "Loading…", isIntermediate: true, lockedMessage: "" };
    return [
      ...formSteps.slice(0, insertAt),
      loadingStep,
      ...formSteps.slice(insertAt),
    ];
  }, [stepperInBetween, baseStepIndex, formSteps]);

  // Keep the small stage message updated
  useEffect(() => {
    const msg = inBetweenStagesLoaderOptions[form.status] || "Loading...";
    setInBetweenStagesLoaderMessage(msg);
  }, [form.status]);

  // Update URL stage param and visited steps when contentStep changes
  const setStage = useCallback(
    (newStage) => {
      const current = new URLSearchParams(history.location.search).get("stage");
      if (String(newStage) !== current) {
        history.push(`?stage=${newStage}`);
      }
    },
    [history]
  );

  useEffect(() => {
    setStage(contentStep);
    setVisitedSteps((prev) => (prev.includes(contentStep) ? prev : [...prev, contentStep]));
  }, [contentStep, setStage]);

  // Fetch form details on mount / formID change
  useEffect(() => {
    async function fetchFormDetails() {
      try {
        setBoardLoading({ show: true, type: "fetch", message: "Loading form details..." });
        setStage(0);
        const formData = new FormData();
        formData.append("json_project_id", formID);
        const data = await getJSONProjectAPI(formData);
        const latestVersion = data?.markdown_md?.length || 0;
        const markdown = data?.markdown_md?.at(latestVersion - 1)?.markdown || "";
        const latestJsonVersion = data?.json_design?.length || 0;
        const latestJsonObj = data?.json_design?.at(latestJsonVersion - 1)?.json || {
          "version": 1,
          "json": {},
          "timestamp": ""
        };
        setForm(data);
        setCurrentMarkdownVersion(latestVersion);
        setLatestMarkdown(markdown);
        setOriginalMarkdownContent(markdown);
        setCurrentJsonVersion(latestJsonVersion);
        setLatestJsonObj(latestJsonObj);
        const projects = await fetchAllProjectsAPI();
        setProjects(projects.map((project) => ({
          name: project.name,
          id: project._id,
        })));

        const devValProjects = await getUserDevValProjectsAPI();
        setUserDevValProjects(devValProjects.map((project) => ({
          name: project.title,
          _id: project._id,
        })));

        const step = data.status;
        const stepIndex = statusToStepMapping[step] ?? statusToStepMapping.default;

        setFormCompletedTillStep(stepIndex);
        setContentStep(stepIndex);

        if (["Review", "Published", "Notebook Review"].includes(step)) {
          setFormGeneration(true);
        }

        setFormName(data.name);
      } catch (error) {
        console.error("Error in fetchFormDetails:", error);
        showNotification("Error fetching form details", "danger");
      } finally {
        setBoardLoading({ show: false, type: "", message: "" });
      }
    }
    fetchFormDetails();
  }, [formID]);

  // Auth
  useEffect(() => {
    async function fetchUser() {
      try {
        const u = await Auth.currentAuthenticatedUser();
        setUser(u);
      } catch (error) {
        console.error("Error in fetchUser:", error);
        showNotification("Error fetching user details", "danger");
      }
    }
    fetchUser();
  }, [showNotification]);


  const handleStepClick = (uiIndex) => {
    const isIntermediate = stepperInBetween && uiIndex === baseStepIndex + 1;

    if (isIntermediate) {
      // Clicking the loading bubble restores the content loader
      setSuppressContentOnly(false);
      return;
    }

    // Map UI index to real content index (skip the inserted loading bubble)
    const realIndex =
      stepperInBetween && uiIndex > baseStepIndex ? uiIndex - 1 : uiIndex;

    // ✅ Allow any step up to what’s completed
    const canAccess = realIndex <= formCompletedTillStep;
    if (!canAccess) {
      const message = formSteps[realIndex]?.lockedMessage || "This step is locked.";
      showNotification(message, "danger");
      return;
    }

    // If loading and user is going to a previous/at step, hide only the content loader
    if (isInBetween && realIndex <= baseStepIndex) {
      setSuppressContentOnly(true);
    }

    setContentStep(realIndex);
  };


  // Content
  const IntermediateContent = () => (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="center"
      minHeight="50vh"
      flexDirection="column"
      p={2}
    >
      <CircularProgress className={classes.loaderClass} />
      {/* BIG: expected time */}
      <Typography variant="h6" style={{ marginTop: 16, textTransform: "none" }}>
        {WAIT_MSG || "Working on it…"}
      </Typography>
      {/* SMALL: specific stage message */}
      {inBetweenStagesLoaderMessage && (
        <Typography
          variant="body2"
          style={{ marginTop: 8, opacity: 0.8, textTransform: "none" }}
        >
          {inBetweenStagesLoaderMessage}
        </Typography>
      )}
    </Box>
  );

  const [fileChanged, setFileChanged] = useState(false);

  const onProjectDetailsSave = async (data) => {
    setBoardLoading({ show: true, type: "save", message: "Saving details..." });
    try {
      data.associated_project_id = data.associated_project_id;

      const formData = new FormData();
      formData.append("json_project_id", formID);
      formData.append("name", data.name);
      formData.append("description", data.description);
      formData.append("associated_project_id", data.associated_project_id);

      setForm((prev) => ({
        ...prev,
        name: data.name,
        description: data.description,
        associated_project_id: data.associated_project_id,
        linked_project_id: data.linked_project_id,
        file: data.file
      }))
      // if the file has changed
      if (data.linked_project_id) {
        formData.append("linked_project_id", data.linked_project_id);
      }
      else if (fileChanged && data.file) {
        formData.append("file", data.file);
        setForm((prev) => ({
          ...prev,
          file: data.file
        }))
      }
      await updateJSONProjectAPI(formData);
      showNotification("Details updated successfully", "success");
    } catch (error) {
      showNotification(error, "danger");
    } finally {
      setBoardLoading({ show: false, type: "", message: "" });
      window.location.reload();
    }
  };

  const onProjectDetailsNext = async (data) => {
    setBoardLoading({ show: true, type: "next", message: "Saving details..." });
    await onProjectDetailsSave(data);
    const formData = new FormData();
    formData.append("json_project_id", formID);
    formData.append("next_step", 1);
    await setNextStepJsonProjectAPI(formData);
    showNotification("Details updated successfully", "success");
    setContentStep(1);
    setBoardLoading({ show: false, type: "", message: "" });
    window.location.reload();
  }


  const onReviewScreenSave = async () => {
    setBoardLoading({ show: true, type: "save", message: "Saving details..." });
    const formData = new FormData();
    formData.append("json_project_id", formID);
    formData.append("markdown", latestMarkdown);
    setForm((prev) => ({
      ...prev,
      markdown_md: [...prev.markdown_md, { markdown: latestMarkdown, timestamp: new Date().toISOString(), version: prev.markdown_md.length + 1 }]
    }))
    await saveNewMDInJSONProjectAPI(formData);
    setCurrentMarkdownVersion(prev => prev + 1);
    showNotification("New Content saved successfully", "success");
    setBoardLoading({ show: false, type: "", message: "" });
  }

  const onReviewScreenNext = async () => {
    // save if the markdown is updated
    if (latestMarkdown !== form.markdown_md.at(currentMarkdownVersion - 1)['markdown']) {
      await onReviewScreenSave();
    }
    setBoardLoading({ show: true, type: "next", message: "Converting template. This might take a few seconds..." });
    const formData = new FormData();
    formData.append("json_project_id", formID);
    await convertMarkdownToSurveyJSFormAPI(formData);
    formData.append("next_step", 2);
    await setNextStepJsonProjectAPI(formData);
    showNotification("Details updated successfully", "success");
    setContentStep(2);
    setBoardLoading({ show: false, type: "", message: "" });
    window.location.reload();
  }



  const onReviewJsonSaveVersion = async (json) => {
    setBoardLoading({ show: true, type: "save", message: "Saving details..." });
    const formData = new FormData();
    formData.append("json_project_id", formID);
    formData.append("json_form", JSON.stringify(json));
    setForm((prev) => ({
      ...prev,
      json_design: [...prev.json_design, { json: json, timestamp: new Date().toISOString(), version: prev.json_design.length + 1 }]
    }))
    setCurrentJsonVersion(prev => prev + 1);
    setLatestJsonObj(json);
    await saveNewJSONDesignInJSONProjectAPI(formData);
    showNotification("New Version saved successfully", "success");
    setBoardLoading({ show: false, type: "", message: "" });
  }

  const onReviewJsonNext = async () => {
    if (latestJsonObj !== form.json_design.at(currentJsonVersion - 1)['json']) {
      await onReviewJsonSaveVersion(latestJsonObj);
    }

    setBoardLoading({ show: true, type: "next", message: "Converting template. This might take a few seconds..." });
    const formData = new FormData();
    formData.append("json_project_id", formID);
    formData.append("next_step", 3);
    await setNextStepJsonProjectAPI(formData);
    showNotification("Details updated successfully", "success");
    setContentStep(3);
    setBoardLoading({ show: false, type: "", message: "" });
    window.location.reload();
  }

  useEffect(() => {
    setLatestMarkdown(form.markdown_md.at(currentMarkdownVersion - 1)['markdown']);
  }, [currentMarkdownVersion]);

  const handleUnpublishForm = async () => {
    setBoardLoading({ show: true, type: "unpublish", message: "Unpublishing form. This might take a few seconds..." });
    const formData = new FormData();
    formData.append("json_project_id", formID);
    await unPublishFormAPI(formData);
    setForm((prev) => ({
      ...prev,
      published_form_link: ""
    }))
    showNotification("Template unpublished successfully", "success");
    setBoardLoading({ show: false, type: "", message: "" });
    window.location.reload();
  }

  const renderContent = () => {
    if (contentInBetween) return <IntermediateContent />;

    const stepLabel = formSteps[contentStep]?.label;
    if (!stepLabel) return <></>;

    switch (stepLabel) {
      case "Project Details":
        return (
          <ProjectDetails formID={formID} project={{
            name: form.name,
            description: form.description,
            associated_project_id: form.associated_project_id,
            file: { name: form?.uploaded_files?.[0]?.resource_name || "" },
            linked_project_id: form?.linked_project_id || ""
          }}
            associatedProjectsFromParent={projects}
            devValProjectsFromParent={userDevValProjects}
            onSave={onProjectDetailsSave}
            onNext={onProjectDetailsNext}
            loading={boardLoading.show}
            setFileChanged={setFileChanged}
            setActiveStep={setContentStep} />
        );
      case "Extract Content":
        return (
          <ReviewScreen
            title="Extract Content"
            description="Review the extracted content from the template. You can use the feedback box to update the content using natural language."
            dropdown_label=""
            dropdown_options={null}
            onDropDownSelection={null}
            isMultiSelect={false}
            nextLabel="Next"
            markdownContent={latestMarkdown}
            setMarkdownContent={setLatestMarkdown}
            version={currentMarkdownVersion}
            versionOptions={form["markdown_md"]}
            setVersion={setCurrentMarkdownVersion}
            onNext={onReviewScreenNext}
            onSave={onReviewScreenSave}
            setLoading={setBoardLoading}
            originalMarkdownContent={originalMarkdownContent}
          />
        );
      case "Review Form":
        return (
          <ReviewJson formID={formID} jsonDataArray={form.json_design} onSaveVersion={onReviewJsonSaveVersion} onClickNext={onReviewJsonNext} />
        );
      case "Preview Form":
        return (
          <PreviewForm formID={formID} json={latestJsonObj} onClickNext={onPreviewFormNext} setActiveStep={setContentStep} version={currentJsonVersion} />
        );
      case "Form Entries":
        return (
          <FormEntries formID={formID} setActiveStep={setContentStep} published_form_link={form.published_form_link} onUnpublishForm={handleUnpublishForm} />
        );
      default:
        return <></>;
    }
  };

  const onPreviewFormNext = async () => {
    setBoardLoading({ show: true, type: "next", message: "Publishing form. This might take a few seconds..." });
    const formData = new FormData();
    formData.append("json_project_id", formID);
    await publishFormAPI(formData);
    formData.append("next_step", 4);

    await setNextStepJsonProjectAPI(formData);
    showNotification("Template published successfully", "success");
    setContentStep(4);
    setBoardLoading({ show: false, type: "", message: "" });
    window.location.reload();
  }

  if (boardLoading.show) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress className={classes.loaderClass} />
        <Typography variant="h6" style={{ marginLeft: 16, textTransform: "none" }}>
          {boardLoading.message}
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Divider style={{ marginBottom: 16 }} />
      <HeadingWithDescription
        title={formName}
        shortDescription={form.description}
        identifier="form"
        dialogTitle="Edit Form Details"
        showEditIcon={false}
        onChange={async (updatedData) => {
          setFormName(updatedData.title);
          setForm((prev) => ({
            ...prev,
            name: updatedData.title,
            description: updatedData.shortDescription,
          }));
          const formData = new FormData();
          formData.append("json_project_id", formID);
          formData.append("name", updatedData.title);
          formData.append("description", updatedData.shortDescription);
          await updateJSONProjectAPI(formData);
          showNotification("Form details updated successfully", "success");
        }}
      />

      <AppBar 
        position="static" 
        className={classes.header}
        style={{
          border: "2px solid #20677b",
          borderRadius: "6px",
          margin: 0,
          marginTop: -16,
          padding: 0,
          minHeight: "40px",
        }}
      >
        <Toolbar style={{ minHeight: "40px", padding: "4px 8px" }}>
          <Box display="flex" justifyContent="space-between" width="100%" alignItems="center">
            {/* Stepper */}
            <Stepper
              activeStep={visualActiveStep}
              connector={<QontoConnector />}
              size="small"
              className={classes.stepperRoot}
              style={{
                flexGrow: 1,
                margin: 0,
                padding: "4px 0",
              }}
            >
              {stepsWithIntermediate.map((step, index) => {
                const isIntermediate = !!step.isIntermediate;
                const isDisabled = isIntermediate
                  ? false // allow clicking the loading bubble to restore content loader
                  : index >
                  (stepperInBetween ? formCompletedTillStep + 1 : formCompletedTillStep);

                return (
                  <Step 
                    key={`${step.label}-${index}`} 
                    onClick={() => handleStepClick(index)}
                    style={{ padding: "0 4px" }}
                  >
                    <StepLabel
                      StepIconComponent={isIntermediate ? LoadingStepIcon : undefined}
                      StepIconProps={
                        !isIntermediate && !isDisabled
                          ? {
                            classes: {
                              root: classes.stepIcon,
                              active: classes.activeStepIcon,
                              completed: classes.completedStepIcon,
                            },
                          }
                          : {}
                      }
                      sx={{
                        "& .MuiStepLabel-label": {
                          display: "none",
                        },
                        "&:hover .MuiStepLabel-label": {
                          display: "block",
                          position: "absolute",
                          backgroundColor: "#20677b",
                          color: "white",
                          padding: "4px 8px",
                          borderRadius: "4px",
                          fontSize: "0.7rem",
                          whiteSpace: "nowrap",
                          marginTop: "8px",
                          zIndex: 1000,
                          boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
                        },
                        "& .MuiStepLabel-iconContainer": {
                          paddingRight: 0,
                        },
                      }}
                    >
                      {step.label}
                    </StepLabel>
                  </Step>
                );
              })}
            </Stepper>
          </Box>
        </Toolbar>
      </AppBar>

      <Box mt={3}>{renderContent()}</Box>
    </Box>
  );
}
